import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useId } from 'react';
import { LucideIcon } from '../UI/LucideIcon';

// Sub-menu da página (Painel, Detalhe da emissão): ícone + nome de cada aba
// numa linha fina; a ativa em índigo/negrito com uma listra índigo embaixo.
// `children` = conteúdo da aba ativa, logo abaixo. Estilos em lev.css
// (`.lev-page-tabs*`; dropdown do "Mais" = `.lev-dropdown`).
//  - Cada aba é rota: <a href> com role="tab" + aria-current="page"
//    (Ctrl/⌘+clique abre em nova aba). Foco "roving": só a ativa entra no
//    Tab; ←/→ movem o foco entre as abas, Home/End vão pra primeira/última.
//  - Não coube na largura (desktop): o excedente vai pra um último item
//    "Mais ▾". A ativa nunca vai pro "Mais" — troca de lugar com a última
//    visível. No mobile (<768px) não tem "Mais": a linha rola na horizontal.
// `abas`: [{ id, label, icon? }]; `hrefDe(aba)` → path; `onSelect(aba)` navega.

const MOBILE_MQ = '(max-width: 767px)';

function useMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

// Fecha um popup com Esc e com clique fora de `ref` — compartilhado com o
// dropdown da NavBar.
export function useFecharPopup(aberto, ref, fechar) {
  useEffect(() => {
    if (!aberto) return undefined;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) fechar(); };
    const onKey = e => { if (e.key === 'Escape') fechar(true); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [aberto, ref, fechar]);
}

// ↑/↓/Home/End entre os itens de um painel de menu (role="menuitem").
export function navegarMenu(e, painel) {
  if (!painel || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
  // Pula itens escondidos (ex. submenu lateral fechado, NavBar) — focus() neles não faz nada.
  const itens = [...painel.querySelectorAll('[role="menuitem"]')].filter(el => getComputedStyle(el).visibility !== 'hidden');
  if (!itens.length) return;
  e.preventDefault();
  const i = itens.indexOf(document.activeElement);
  const prox = e.key === 'Home' ? 0
    : e.key === 'End' ? itens.length - 1
    : e.key === 'ArrowDown' ? (i + 1) % itens.length
    : (i <= 0 ? itens.length - 1 : i - 1);
  itens[prox].focus();
}

// Clique "normal" (sem modificador, botão esquerdo) navega pela SPA; o resto
// fica com o navegador (nova aba etc.).
export function cliqueSpa(e) {
  return !(e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
}

function MaisMenu({ abas, hrefDe, onSelect }) {
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const painelRef = useRef(null);
  const fechar = useCallback(foco => { setAberto(false); if (foco) btnRef.current?.focus(); }, []);
  useFecharPopup(aberto, wrapRef, fechar);
  const abrirEFocar = () => {
    setAberto(true);
    requestAnimationFrame(() => painelRef.current?.querySelector('[role="menuitem"]')?.focus());
  };
  return (
    <div className="lev-page-tabs-mais" ref={wrapRef}>
      <button ref={btnRef} type="button" className="lev-page-tab" data-aba-foco tabIndex={-1}
        role="tab" aria-selected="false" aria-haspopup="menu" aria-expanded={aberto}
        onClick={() => setAberto(v => !v)}
        onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); abrirEFocar(); } }}>
        Mais
        <LucideIcon name="chevron-down" size={14} className="lev-chevron" style={{ transform: aberto ? 'rotate(180deg)' : 'none' }}/>
      </button>
      <div ref={painelRef} className={`lev-dropdown lev-dropdown-direita${aberto ? ' aberto' : ''}`} role="menu"
        aria-label="Mais abas" onKeyDown={e => navegarMenu(e, painelRef.current)}>
        {abas.map(aba => (
          <a key={aba.id} href={hrefDe(aba)} role="menuitem" tabIndex={aberto ? 0 : -1} className="lev-dropdown-item"
            onClick={e => { if (!cliqueSpa(e)) return; e.preventDefault(); setAberto(false); onSelect(aba); }}>
            {aba.icon && <LucideIcon name={aba.icon} size={16}/>}
            {aba.label}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function PageTabs({ abas, ativa, hrefDe, onSelect, label = 'Seções da página', children }) {
  const mobile = useMobile();
  const uid = useId();
  const linhaRef = useRef(null);
  const medidaRef = useRef(null);
  // null = cabem todas; n = n primeiras (já com a ativa trocada pra dentro).
  const [nVisiveis, setNVisiveis] = useState(null);

  const idxAtiva = abas.findIndex(a => a.id === ativa);
  // Ordem efetiva: se a ativa ficaria no "Mais", troca com a última visível.
  const ordem = (() => {
    if (nVisiveis == null || idxAtiva < nVisiveis || idxAtiva < 0) return abas;
    const o = abas.slice();
    [o[nVisiveis - 1], o[idxAtiva]] = [o[idxAtiva], o[nVisiveis - 1]];
    return o;
  })();
  const visiveis = nVisiveis == null ? ordem : ordem.slice(0, nVisiveis);
  const noMais = nVisiveis == null ? [] : ordem.slice(nVisiveis);
  const posAtiva = visiveis.findIndex(a => a.id === ativa);
  const idTab = id => `${uid}-aba-${id}`;

  const medir = useCallback(() => {
    const linha = linhaRef.current, medida = medidaRef.current;
    if (!linha || !medida) return;
    if (mobile) { setNVisiveis(null); return; }
    const itens = [...medida.children];
    const larg = itens.slice(0, abas.length).map(el => el.offsetWidth);
    const largMais = itens[abas.length]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(linha).columnGap) || 0;
    const disponivel = linha.clientWidth;
    const soma = ids => ids.reduce((s, i) => s + larg[i], 0) + gap * Math.max(0, ids.length - 1);
    const todos = abas.map((_, i) => i);
    if (soma(todos) <= disponivel) { setNVisiveis(null); return; }
    // Maior n em que as n visíveis (com a ativa trocada pra dentro) + "Mais" cabem.
    let n = abas.length - 1;
    for (; n > 1; n--) {
      const ids = todos.slice(0, n);
      if (idxAtiva >= n) ids[n - 1] = idxAtiva;
      if (soma(ids) + gap + largMais <= disponivel) break;
    }
    setNVisiveis(Math.max(1, n));
  }, [abas, idxAtiva, mobile]);

  useLayoutEffect(() => {
    medir();
    const ro = new ResizeObserver(medir);
    if (linhaRef.current) ro.observe(linhaRef.current);
    return () => ro.disconnect();
  }, [medir]);

  // Mobile: rola a linha até a aba ativa (só na horizontal — scrollIntoView
  // poderia rolar a página junto).
  useEffect(() => {
    const linha = linhaRef.current;
    if (!mobile || !linha) return;
    const el = linha.querySelector('[aria-current="page"]');
    if (!el) return;
    const esq = el.offsetLeft - linha.offsetLeft;
    if (esq < linha.scrollLeft || esq + el.offsetWidth > linha.scrollLeft + linha.clientWidth) {
      linha.scrollLeft = esq - 16;
    }
  }, [mobile, ativa]);

  const onKeyDown = e => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const focaveis = [...linhaRef.current.querySelectorAll(':scope > [data-aba-foco], :scope > .lev-page-tabs-mais > [data-aba-foco]')];
    const i = focaveis.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const prox = e.key === 'Home' ? 0
      : e.key === 'End' ? focaveis.length - 1
      : e.key === 'ArrowRight' ? Math.min(focaveis.length - 1, i + 1)
      : Math.max(0, i - 1);
    focaveis[prox].focus();
  };

  return (
    <div className="lev-page-tabs-wrap">
      <nav ref={linhaRef} className="lev-page-tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {visiveis.map((aba, i) => {
          const eAtiva = aba.id === ativa;
          return (
            <a key={aba.id} id={idTab(aba.id)} href={hrefDe(aba)} data-aba-foco className="lev-page-tab"
              role="tab" aria-selected={eAtiva} aria-current={eAtiva ? 'page' : undefined}
              tabIndex={eAtiva || (posAtiva < 0 && i === 0) ? 0 : -1}
              onClick={e => { if (!cliqueSpa(e)) return; e.preventDefault(); onSelect(aba); }}>
              {aba.icon && <LucideIcon name={aba.icon} size={15}/>}
              {aba.label}
            </a>
          );
        })}
        {noMais.length > 0 && <MaisMenu abas={noMais} hrefDe={hrefDe} onSelect={onSelect}/>}
        {/* Réplica invisível só pra medir a largura natural de cada aba (e do
            "Mais"), independente de quais estão visíveis agora. */}
        <div ref={medidaRef} className="lev-page-tabs-medida" aria-hidden="true">
          {abas.map(aba => (
            <span key={aba.id} className="lev-page-tab" aria-current={aba.id === ativa ? 'page' : undefined}>
              {aba.icon && <LucideIcon name={aba.icon} size={15}/>}{aba.label}
            </span>
          ))}
          <span className="lev-page-tab">Mais<LucideIcon name="chevron-down" size={14}/></span>
        </div>
      </nav>
      <section className="lev-page-tabs-panel"
        role="tabpanel" aria-labelledby={posAtiva >= 0 ? idTab(ativa) : undefined}>
        {children}
      </section>
    </div>
  );
}
